import {
    Stack, getTheme, Separator, Text, PrimaryButton, SharedColors,
    FontIcon, DirectionalHint, IconButton, TooltipDelay,
    TooltipHost, ICalloutContentStyles
} from "@fluentui/react"
import { useId } from "@uifabric/react-hooks";
import React from "react";
import { strings } from "../../../../../localization/strings";
import Device from "./Device";
import * as UIKit from "@piceasoft/picea-react-ui-kit"
import { PreviewStatesToggler, TPreviewState } from "../identification/preview/ui/PreviewStatesToggler";
import { IStageConfig, ProcessStages, IStageConfigUI } from "@piceasoft/core";

type TProps = {
    config: IStageConfig<IStageConfigUI>
    stage: ProcessStages
    defaultNextText: string
    defaultCancelText?: string
    defaultBackText?: string
    defaultStageTitle?: string
    previewStates?: TPreviewState[]
    previewStateSelected: (index: number) => void
    noTitle?: boolean
    noTransaction?: boolean
    noDevice?: boolean
    noNext?: boolean
}

export const StagePreviewUIKit: React.FC<TProps> = ({ config, ...props }) => {

    const tooltipId = useId('tooltip');

    return (
        <Stack style={{ backgroundColor: 'rgba(0, 0, 0, 0.4)' }} >
            { props.previewStates && (
            <Stack horizontalAlign="center" styles={ { root:{paddingTop: '6px'}}}>
                <PreviewStatesToggler states={props.previewStates} onSelectState={props.previewStateSelected}/>
            </Stack>)}
            <Stack.Item tokens={{ padding: 24 }}>
                <Stack style={{ backgroundColor: theme.palette.neutralLighter, boxShadow: theme.effects.elevation64 }} >
                    <Stack.Item>
                        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 4 }} styles={{ root: { padding: "4px 16px 4px 24px", backgroundColor: theme.palette.white } }}>
                            <Stack.Item grow>
                                <Stack horizontal verticalAlign="center" styles={{ root: { height: 56 } }}>
                                    <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 16 }}>
                                        <FontIcon iconName="Sprint" style={{ fontSize: 24, height: 24, width: 24 }} />
                                        <Separator vertical />
                                        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
                                            { ProcessStages.Identification !== props.stage && !props.noTitle && (config?.ui?.stageTitle || props.defaultStageTitle) && (
                                                <Text variant="mediumPlus" style={{ color: SharedColors.gray30 }}>{config?.ui?.stageTitle ?? props.defaultStageTitle}</Text>
                                            )}
                                            { ![ProcessStages.Identification, ProcessStages.PreOffer].includes(props.stage) && !props.noTransaction &&
                                                <TooltipHost
                                                    calloutProps={{ styles: tooltipCalloutStyles }}
                                                    content={strings.BUTTONS.TEXT.COPY}
                                                    delay={TooltipDelay.medium}
                                                    id={tooltipId}
                                                    directionalHint={DirectionalHint.bottomCenter}
                                                >
                                                    <IconButton iconProps={{ iconName: 'Copy' }} ariaLabel="Copy" />
                                                </TooltipHost>}
                                        </Stack>
                                        {![ProcessStages.Identification, ProcessStages.PreOffer].includes(props.stage) && !props.noDevice && (
                                            <>
                                                <Separator vertical />
                                                <Device />
                                            </>
                                        )}
                                    </Stack>
                                </Stack>
                            </Stack.Item>
                            <Stack.Item>
                                <Stack horizontal verticalFill verticalAlign='center' tokens={{ childrenGap: 8 }}>
                                    { (config.ui?.buttons?.back?.text || props.defaultBackText) && 
                                    (<Stack.Item>
                                        <UIKit.OnlineButton color="secondary" onClickHandler={ () => {}}>{config.ui?.buttons?.back?.text ?? props.defaultBackText}</UIKit.OnlineButton>
                                    </Stack.Item>)
                                    }
                                    { (config.ui?.buttons?.cancel?.text || props.defaultCancelText) && 
                                    (<Stack.Item>
                                        <UIKit.OnlineButton color="secondary" onClickHandler={ () => {}}>{config.ui?.buttons?.cancel?.text ?? props.defaultCancelText}</UIKit.OnlineButton>
                                    </Stack.Item>)
                                    }
                                    { !props.noNext && 
                                    (<Stack.Item>
                                        <UIKit.OnlineButton color="primary" onClickHandler={ () => {}}>{config.ui?.buttons?.next?.text ?? props.defaultNextText}</UIKit.OnlineButton>
                                    </Stack.Item> )
                                    }
                                </Stack>
                            </Stack.Item>
                        </Stack>
                        {props.children}
                    </Stack.Item>
                </Stack>
            </Stack.Item>
        </Stack>
    )
}

const theme = getTheme();

const tooltipCalloutStyles: Partial<ICalloutContentStyles> = {
    beakCurtain: { background: theme.palette.neutralLight },
    beak: { background: theme.palette.neutralLight },
    calloutMain: { background: theme.palette.neutralLight },
    root: { color: theme.palette.white },
    container: { color: 'white' }
}